import { useTranslation } from 'react-i18next';
import { ICellRendererParams } from 'ag-grid-community';
import { z } from 'zod';

import { isSupportedFileIcon, SVGIcon } from '../../icons/SVGIcon';
import { TextWithTooltip } from '../../tooltip/TextWithTooltip';
import { EMPTY_VALUE } from '../valueFormatters';

import { DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED } from './constants';

import styles from './fileDownloadCellRenderer.module.scss';

// todo: align with WorkflowStepFile (or specify in portal-gateway)
const FileDto = z.object({
  id: z.string(),
  fileName: z.string(),
  downloadToken: z.string().nullable(),
  mimetype: z.string(),
  virusFree: z.boolean(),
});

const Files = z.array(FileDto);

type FileDto = z.infer<typeof FileDto>;

export type DownloadFiles = z.infer<typeof Files>;

function isFilesData(data: unknown): data is DownloadFiles {
  return Files.safeParse(data).success;
}

export interface IFileDownloadCellRendererParams<TData = unknown>
  extends ICellRendererParams<TData, DownloadFiles> {
  onFileClick?: (file: FileDto) => void;
}

export const FileDownloadCellRenderer = ({
  formatValue,
  onFileClick,
  value,
}: IFileDownloadCellRendererParams) => {
  const { t } = useTranslation();

  if (value === undefined) {
    return;
  }

  if (value == null || (Array.isArray(value) && value.length === 0)) {
    return formatValue?.(value) ?? EMPTY_VALUE;
  }

  if (!isFilesData(value)) {
    throw new Error(
      `FileDownloadCellRenderer called with wrong data format: ${JSON.stringify(value, null, 2)}`
    );
  }

  return (
    <div className={styles['root']}>
      {value?.map((file) => {
        const virusInfected = !file.virusFree;
        const allowFileDownload = onFileClick && !virusInfected;

        return (
          <div
            {...DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED}
            data-testid={`file-download-cell-renderer-file_${file.id}`}
            onClick={allowFileDownload ? () => onFileClick(file) : undefined}
            key={file.id}
          >
            {virusInfected ? (
              <TextWithTooltip
                tooltipProps={{
                  disabled: false,
                  label: t('app.table.tooltip.maliciousContent'),
                }}
                style={{ display: 'flex' }}
              >
                <SVGIcon name="file-alert" />
              </TextWithTooltip>
            ) : (
              <TextWithTooltip
                tooltipProps={{ disabled: false, label: file.fileName }}
                style={{ cursor: 'pointer', display: 'flex' }}
              >
                <SVGIcon name={mapFileNameToSVGFileIcon(file.fileName)} />
              </TextWithTooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};

function mapFileNameToSVGFileIcon(fileName: string) {
  const fileIconName = `file-${fileName.split('.').pop()?.toLowerCase()}`;
  return isSupportedFileIcon(fileIconName) ? fileIconName : 'file-unknown';
}
